import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  type Breakpoint,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog as MuiDialog,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import type React from 'react';
import type { FC } from 'react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: Breakpoint;
}

const Dialog: FC<DialogProps> = ({ open, onClose, title, children, actions, maxWidth = 'md' }) => {
  const theme = useTheme();

  if (!open) {
    return null;
  }

  return (
    <MuiDialog
      open
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
          px: 3
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: 'error.main'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>{children}</Box>
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.grey[500], 0.04)
          }}
        >
          {actions}
        </DialogActions>
      )}
    </MuiDialog>
  );
};

export default Dialog;
